import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {
  MtsBadge,
  MtsButton,
  MtsDivider,
  MtsLink,
  useMtsModal
} from '@mts-ds/components-react';
import {
  ButtonColors,
  ButtonFormats,
  ButtonSizes
} from '@mts-ds/components/dist/types/components/button/enums';

import { Dispatch, iRootState } from '../../../../../../store';
import { IUser, INotificationClass, IUserStatus } from '../../../../../../models/types';
import { IData } from '../../../../../../models/infrastructureUsers';
import { paths } from '../../../../../Root/paths';
import getFullName from '../../../../../../utils/getFullName';
import { getClearStateErrorText, getSubjectStateLabel, getUserStatuses } from '../../utils';

import Typography from '../../../../../../components_/Typography';
import Tooltip from '../../../../../../components_/Tooltip';
import Icon from '../../../../../../components_/Icon';
import NotificationIcon from '../../../../../../components_/Notification/NotificationIcon';
import Modal from '../../../../../../components_/Modal';
import { ToastContext } from '../../../../../../components_/Toast';

const useStyles = makeStyles(({ spacing, getColor }) => ({
  info: {
    marginTop: spacing(1),
    marginLeft: spacing(8),
    '& > *:not(:last-child)': {
      marginBottom: spacing(1)
    }
  },
  label: {
    '& > *:not(:last-child)': {
      marginRight: spacing(1)
    }
  },
  pulseLabel: {
    color: getColor('icon-secondary')
  },
  states: {
    '& > *:not(:last-child)': {
      marginBottom: spacing(1)
    }
  },
  stateRow: {
    minWidth: 0
  },
  stateContent: {
    minWidth: 0,
    '& > *:not(:last-child)': {
      marginRight: spacing(1)
    }
  },
  protections: {
    '& > *:not(:last-child)': {
      marginBottom: spacing(2)
    }
  },
  divider: {
    margin: spacing(2, 0)
  }
}));

enum HealthTypes {
  PULSE = 'PULSE',
  DEVICE_CHARGE = 'DEVICE_CHARGE',
  DEVICE_IS_CHARGING = 'DEVICE_IS_CHARGING'
}

const mapState = (state: iRootState) => ({
  userRole: state.user.role
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    clearUserState: d.infrastructureUsers.clearUserState
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

interface IProps {
  user: IUser;
  data: IData;
  isTabularNumberUsed: boolean;
  isPulseUsed: boolean;
  isProtectionsUsed: boolean;
}

type Props = IProps & connectedProps;

const Attributes: FC<Props> = ({
  user,
  data,
  isTabularNumberUsed,
  isPulseUsed,
  isProtectionsUsed,
  userRole,
  clearUserState
}) => {
  const {
    descr,
    tabularNumber,
    phone,
    lead,
    protectionsList: userProtectionIds,
    trackInfo,
    charge,
    rootDepartmentName,
    departmentName,
    rootDepartmentId,
    departmentId
  } = user;

  const { states = [], measurements = [], online = false, tag } = trackInfo || {};
  const { value: chargepercent } =
    (measurements && measurements.find(({ type }) => type === HealthTypes.DEVICE_CHARGE)) || {};
  const { percent: percentCharge } = charge || {};

  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
  const visibleStates = getUserStatuses(states, { includeHidden: isAdmin });

  const stateToClearRef = useRef<IUserStatus | null>(null);
  const clearingInFlightRef = useRef(false);
  const openClearModalRef = useRef<() => void>(() => undefined);
  const [stateToClear, setStateToClear] = useState<IUserStatus | null>(null);
  const [openClearRequested, setOpenClearRequested] = useState(false);
  const [clearingStateId, setClearingStateId] = useState<string | null>(null);

  const { protections: dataProtections } = data;

  const classes = useStyles();
  const history = useHistory();
  const toast = useContext(ToastContext);

  const [openClearModal] = useMtsModal(Modal, {
    header: 'Снять состояние',
    text: stateToClear
      ? `Снять состояние «${getSubjectStateLabel(stateToClear)}» у сотрудника ${getFullName(
          user,
          false
        )}?`
      : '',
    buttonTextConfirm: 'Снять',
    onMtsModalConfirm: async () => {
      const current = stateToClearRef.current;
      if (!current?.stateId || clearingInFlightRef.current) {
        return;
      }

      clearingInFlightRef.current = true;
      setClearingStateId(current.stateId);

      try {
        const error = await clearUserState({ stateId: current.stateId });

        if (error) {
          toast({
            text: getClearStateErrorText(error, 'Ошибка при снятии состояния'),
            type: 'error'
          });
          return;
        }

        toast({ text: 'Состояние снято', type: 'success' });
        stateToClearRef.current = null;
        setStateToClear(null);
      } finally {
        clearingInFlightRef.current = false;
        setClearingStateId(null);
      }
    }
  });

  openClearModalRef.current = openClearModal;

  useEffect(() => {
    if (!openClearRequested || !stateToClear) {
      return;
    }

    setOpenClearRequested(false);
    openClearModalRef.current();
  }, [openClearRequested, stateToClear]);

  const handleClearClick = (state: IUserStatus) => {
    if (!state.stateId) {
      return;
    }

    stateToClearRef.current = state;
    setStateToClear(state);
    setOpenClearRequested(true);
  };

  const handleLeadClick = () => {
    if (lead) {
      history.push(paths.map.office.users.show(lead.username));
    }
  };

  const leadFullName = lead && getFullName(lead, false);

  const protections = isProtectionsUsed
    ? dataProtections.filter(dataProtection => userProtectionIds.includes(dataProtection.id))
    : [];

  const departmentFullName = rootDepartmentName
    ? rootDepartmentName +
      (departmentName && rootDepartmentId !== departmentId ? ' / ' + departmentName : '')
    : departmentName
    ? departmentName
    : '';

  const { value: pulse = '--' } = isPulseUsed
    ? (measurements && measurements.find(({ type }) => type === HealthTypes.PULSE)) || {}
    : {};

  return (
    <>
      <Grid className={classes.info}>
        {isTabularNumberUsed && Boolean(tabularNumber) && (
          <MtsBadge size="s">{`#${tabularNumber}`}</MtsBadge>
        )}
        {Boolean(tag && tag.number) && <MtsBadge size="s">{`#${tag && tag.number}`}</MtsBadge>}
        <Typography variant="p3Regular">{departmentFullName}</Typography>

        <>
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Grid container alignItems="center" className={classes.label}>
                {Number(percentCharge || chargepercent) && online ? (
                  Number(percentCharge || chargepercent) < 20 ? (
                    <Icon name="batteryLow" size="s" color="constant-raspberry-normal" />
                  ) : (
                    <Icon name="battery" size="s" color="constant-apple-normal" />
                  )
                ) : (
                  <Icon name="battery" size="s" color="constant-greyscale-400" />
                )}
                <Typography variant="p3Medium">
                  {Number(percentCharge || chargepercent) || '--'}%
                </Typography>
              </Grid>
            </Grid>
            {isPulseUsed && (
              <Grid item>
                <Grid container alignItems="center" className={classes.label}>
                  <Icon name="heart" size="s" color="constant-orange-normal" />
                  <Typography variant="p3Medium">
                    {pulse} <span className={classes.pulseLabel}>уд./мин</span>
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>

          {Boolean(phone) && (
            <Grid container alignItems="center" className={classes.label}>
              <Icon name="phone" size="s" color="text-secondary" />
              <Typography variant="p3Regular">{phone}</Typography>
            </Grid>
          )}

          {Boolean(descr) && (
            <Grid>
              <Typography variant="p3Regular" color="text-secondary">
                {descr}
              </Typography>
            </Grid>
          )}
        </>

        {visibleStates.length > 0 && (
          <Grid className={classes.states}>
            {visibleStates.map((state, index) => {
              const { ntfy, stateId } = state;
              const notificationClass =
                (ntfy?.class as INotificationClass) || 'NotificationInformational';

              return (
                <Grid
                  key={stateId || `${state.systemName || ntfy?.class || 'state'}-${index}`}
                  container
                  alignItems="center"
                  justify="space-between"
                  wrap="nowrap"
                  className={classes.stateRow}
                >
                  <Grid item xs zeroMinWidth>
                    <Grid container alignItems="center" wrap="nowrap" className={classes.stateContent}>
                      <Grid item>
                        <NotificationIcon notificationClass={notificationClass} size="m" />
                      </Grid>
                      <Grid item zeroMinWidth>
                        <Typography variant="p3Medium">{getSubjectStateLabel(state)}</Typography>
                      </Grid>
                      <Grid item>
                        <MtsBadge size="s">
                          {ntfy?.time ? moment(ntfy.time).format('HH:mm') : '--:--'}
                        </MtsBadge>
                      </Grid>
                    </Grid>
                  </Grid>
                  {isAdmin && (
                    <Grid item>
                      <Tooltip
                        size="s"
                        label={
                          stateId
                            ? 'Снять состояние'
                            : 'Невозможно снять: нет идентификатора состояния'
                        }
                      >
                        <MtsButton
                          size={'s' as ButtonSizes}
                          format={'icon' as ButtonFormats}
                          color={'secondary' as ButtonColors}
                          disabled={!stateId || clearingStateId === stateId}
                          onClick={() => handleClearClick(state)}
                        >
                          <Grid container alignItems="center">
                            <Icon name="bin" size="s" />
                          </Grid>
                        </MtsButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              );
            })}
          </Grid>
        )}
      </Grid>

      <MtsDivider className={classes.divider} />

      {Boolean(leadFullName) && (
        <>
          <Grid container alignItems="center" className={classes.label}>
            <Typography variant="p2Medium">Руководитель</Typography>
            <Typography variant="p2Regular">
              <MtsLink onClick={handleLeadClick}>{leadFullName}</MtsLink>
            </Typography>
          </Grid>

          <MtsDivider className={classes.divider} />
        </>
      )}
      {isProtectionsUsed && (
        <>
          <Grid className={classes.protections}>
            <Grid container alignItems="center" className={classes.label}>
              <Typography variant="p1Bold">Обязательные СИЗ</Typography>
              <MtsBadge type="counter" size="s" counterValue={userProtectionIds.length} />
            </Grid>
            {Boolean(protections.length) && (
              <Grid container spacing={1}>
                {protections.map(({ id, name }) => (
                  <Grid item key={id}>
                    <MtsBadge>{name}</MtsBadge>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          <MtsDivider className={classes.divider} />
        </>
      )}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Attributes);
